
import {
  defineComponent,
  reactive,
  nextTick,
  ref,
  computed,
  onMounted,
  getCurrentInstance,
  toRefs,
  watch
} from 'vue';

import frameworks from '@/data/frameworks.json';
import axios from 'axios';
import { debounce } from 'lodash';
import PromptDialog from './PromptDialog.vue';
import EmployerBrowser from './EmployerBrowser.vue';

export default defineComponent({
  name: 'SetupSiteWizard',
  components: {
    'prompt-dialog': PromptDialog,
    'employer-browser': EmployerBrowser
  },
  props: {
    loading: {
      type: Boolean,
      required: true
    },
    errors: {
      type: Boolean,
      required: true
    },
    defaultStep: {
      type: Number,
      default: 3, // Default to Enter Website step
      validator: value => [1, 2, 3].includes(value)
    }
  },
  setup(props, { emit }) {
    // Destructure props for template access in Vue 2.7x
    const { loading, errors } = toRefs(props);

    const isSponsor = ref(false);
    const currentStep = ref(props.defaultStep);
    const inputWebsite = ref('');
    const loadingDataValue = reactive({
      value: 0,
      message: 'Thanks!\nSuggesting possible program descriptions'
    });

    const instance = getCurrentInstance();
    const root = instance?.proxy;
    const loadingProgress = ref(0);
    const internalLoading = ref(false);
    const showPromptDialog = ref(false);
    const currentSearchData = ref(null);

    // Check if we're in sponsor mode on mount
    onMounted(() => {
      const isSponsorRoute =
        root.$route.path.startsWith('/sponsor/') && root.$route.params?.shareCode;
      if (isSponsorRoute) {
        isSponsor.value = true;
        currentStep.value = 2; // Set initial step for sponsor route
      }
    });

    // Fix navigation functions
    function goToWebsiteStep() {
      currentStep.value = 3;
    }

    function goToResearchStep() {
      // If in sponsor mode, stay on step 2
      if (isSponsor.value) {
        currentStep.value = 2;
      } else {
        currentStep.value = 2;
      }
    }

    function goToCreateProgramStep() {
      currentStep.value = 1;
    }

    // Event handlers for EmployerBrowser component
    async function handleProgramCreated(website) {
      currentStep.value = 3;

      if (website) {
        inputWebsite.value = website;
        await nextTick();
        emit('submitSite', website);
        await loadingData();
      }
    }

    function handleSaveForLater(employer) {
      emit('saveForLater', employer);
    }

    function handleSearchEmployers(query) {
      emit('searchEmployers', query);
    }

    // Add new form state
    const formState = reactive({
      role: '',
      subject: '',
      location: '',
      customLocation: ''
    });

    // Add computed property for course names
    const courseNames = computed(() => {
      const uniqueCourses = new Set();
      frameworks.frameworks.forEach(framework => {
        if (framework.course_name) {
          uniqueCourses.add(framework.course_name);
        }
      });
      return Array.from(uniqueCourses)
        .sort((a, b) => a.localeCompare(b))
        .map(course => ({
          text: course,
          value: course.toLowerCase().replace(/\s+/g, '_')
        }));
    });

    // Add options for each input
    const roleOptions = [
      { text: 'Teacher', value: 'teacher' },
      { text: 'Student', value: 'student' },
      { text: 'Employer', value: 'employer' },
      { text: 'Parent', value: 'parent' },
      { text: 'School', value: 'school' }
    ];

    const subjectOptions = {
      teacher: courseNames.value,
      student: courseNames.value,
      employer: courseNames.value,
      parent: courseNames.value,
      school: courseNames.value
    };

    const locationOptions = [
      { text: 'New York', value: 'new_york' },
      { text: 'Los Angeles', value: 'los_angeles' },
      { text: 'Chicago', value: 'chicago' },
      { text: 'Houston', value: 'houston' },
      { text: 'Phoenix', value: 'phoenix' },
      { text: 'Philadelphia', value: 'philadelphia' },
      { text: 'San Antonio', value: 'san_antonio' },
      { text: 'San Diego', value: 'san_diego' },
      { text: 'Dallas', value: 'dallas' },
      { text: 'San Jose', value: 'san_jose' }
    ];

    // Add computed properties for dynamic options
    const currentSubjectOptions = computed(() => {
      return formState.role ? subjectOptions[formState.role] || [] : [];
    });

    // Add computed property for the built sentence
    const builtSentence = computed(() => {
      let sentence = 'I am a ';

      if (formState.role) {
        const role = roleOptions.find(r => r.value === formState.role);
        sentence += role ? role.text : '';
      } else {
        sentence += '[SELECT ROLE]';
      }

      if (formState.subject) {
        const subject = currentSubjectOptions.value.find(s => s.value === formState.subject);
        sentence += ` that teaches ${subject ? subject.text : ''}`;
      } else if (formState.role) {
        sentence += ' that teaches [SELECT SUBJECT]';
      }

      if (formState.location || formState.customLocation) {
        const location =
          formState.customLocation ||
          (formState.location
            ? locationOptions.find(l => l.value === formState.location)?.text
            : '');
        sentence += ` in ${location}`;
      } else if (formState.subject) {
        sentence += ' in [SELECT LOCATION]';
      }

      return sentence;
    });

    // Add computed property for subject label
    const getSubjectLabel = computed(() => {
      const role = roleOptions.find(r => r.value === formState.role);
      return `Select ${role?.text.toLowerCase()} subject`;
    });

    // Add computed property for form completion
    const isFormComplete = computed(() => {
      return (
        formState.role && formState.subject && (formState.location || formState.customLocation)
      );
    });

    // Add method to handle role selection
    const handleRoleSelect = role => {
      formState.role = role;
      formState.subject = ''; // Reset subject when role changes
    };

    // Add method to handle location selection
    const handleLocationSelect = location => {
      formState.location = location;
      formState.customLocation = ''; // Reset custom location when selecting from list
    };

    // Add method to handle custom location input
    const handleCustomLocation = value => {
      formState.customLocation = value;
      formState.location = ''; // Reset selected location when entering custom
    };

    // Add loading animation function
    const loadingData = async () => {
      internalLoading.value = true;
      await new Promise(resolve => setTimeout(resolve, 2000));
      internalLoading.value = false;
    };

    // Add method to handle form submission
    const handleSubmit = async () => {
      // Find the selected subject
      const selectedSubject = currentSubjectOptions.value.find(
        subject => subject.value === formState.subject
      );
      if (!selectedSubject) return;

      // Get matching framework data
      const matchingFrameworks = frameworks.frameworks.filter(
        f => f.course_name === selectedSubject.text
      );

      if (matchingFrameworks.length > 0) {
        // Store the complete framework data without any modifications
        currentSearchData.value = {
          role: formState.role,
          subject: selectedSubject.text,
          location: formState.customLocation || formState.location,
          frameworks: matchingFrameworks // Store the complete framework objects
        };

        // Show the prompt dialog
        showPromptDialog.value = true;
      }
    };

    const handlePromptProceed = responseData => {
      if (!Array.isArray(responseData)) {
        console.error('Invalid response data:', responseData);
        return;
      }

      // Get the frameworks from the current search data
      const currentFrameworks = currentSearchData.value?.frameworks || [];

      if (currentFrameworks.length === 0) {
        console.error('No framework data available');
        return;
      }

      // Create an array of scope objects with the complete framework data
      const scopes = responseData
        .map(item => {
          // Check if item exists and has required properties
          if (!item || !item.website) {
            console.warn('Invalid item or missing website:', item);
            return null;
          }

          // Find the matching framework by framework_id
          const frameworkData = currentFrameworks.find(f => f._id === item.framework_id);
          if (!frameworkData) {
            console.warn(`No matching framework found for framework_id: ${item.framework_id}`);
            return null;
          }

          // Create scope with properly structured framework data
          const scope = {
            website: item.website,
            framework: {
              id: frameworkData._id,
              course_name: frameworkData.course_name,
              organization_type: frameworkData.organization_type,
              savedInputs: frameworkData.savedInputs || {}
            }
          };
          return scope;
        })
        .filter(Boolean); // Remove any null entries from failed framework matches

      if (scopes.length === 0) {
        console.error('No valid scopes created from response data');
        return;
      }

      // Set the website input with the OpenAI response websites
      inputWebsite.value = scopes
        .map(scope => scope?.website)
        .filter(Boolean)
        .join(', ');

      // Close the dialog
      showPromptDialog.value = false;

      // Navigate to step 3
      currentStep.value = 3;

      // Emit submitSite event with the scopes
      emit('submitSite', {
        websites: inputWebsite.value,
        scopes
      });

      // Start loading animation with a timeout
      internalLoading.value = true;
      setTimeout(() => {
        internalLoading.value = false;
        // Navigate to the bulk setup wizard with the scopes data
        root.$router.push({
          path: '/bulk-scope-setup',
          query: {
            sites: JSON.stringify(scopes.map(scope => scope?.website).filter(Boolean)),
            scopes: JSON.stringify(scopes), // Pass the complete scopes data
            frameworks: JSON.stringify(currentFrameworks) // Pass the complete framework data
          }
        });
      }, 2000);
    };

    // Add method to handle site submission
    const submitSite = async event => {
      // Stop if loading
      if (loading.value || internalLoading.value) return;

      // Stop event propagation and prevent default
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }

      // Strict validation - must have actual content
      if (!inputWebsite.value) return;

      const trimmedValue = inputWebsite.value.trim();
      if (trimmedValue.length === 0) return;

      // Additional validation for URL format
      let validatedUrl;
      try {
        validatedUrl = new URL(
          trimmedValue.startsWith('http') ? trimmedValue : `https://${trimmedValue}`
        );
      } catch (e) {
        return;
      }

      emit('submitSite', validatedUrl.href);
      await nextTick();
      await loadingData();
    };

    // Add computed property to check if website input is valid
    const isValidWebsite = computed(() => {
      return inputWebsite.value && inputWebsite.value.trim().length > 0;
    });

    return {
      // Props (destructured for template access)
      loading,
      errors,
      // Other reactive data
      currentStep,
      inputWebsite,
      loadingDataValue,
      loadingData,
      submitSite,
      goToWebsiteStep,
      goToResearchStep,
      goToCreateProgramStep,
      handleProgramCreated,
      handleSaveForLater,
      handleSearchEmployers,
      isSponsor,
      loadingProgress,
      internalLoading,
      isValidWebsite,
      formState,
      roleOptions,
      currentSubjectOptions,
      locationOptions,
      builtSentence,
      handleRoleSelect,
      handleLocationSelect,
      handleCustomLocation,
      handleSubmit,
      getSubjectLabel,
      isFormComplete,
      courseNames,
      subjectOptions,
      showPromptDialog,
      currentSearchData,
      handlePromptProceed
    };
  }
});
